<template>
  <div class="flex justify-center px-4 md:px-8">
    <div v-if="showcase" class="big-container mt-8 md:mt-0 relative">
      <div class="sky__showcase-illustration mb-6">
        <div class="text-center">
          <div class="text-3xl font-baskerville">
            Notre <span class="font-funny text-4xl">Sélection</span> du moment
          </div>
          <div class="font-geologica font-thin">
            Nos coups de coeur à ne pas manquer
          </div>
        </div>
      </div>
      <button
        v-if="showLeftArrow"
        @click="scrollLeft"
        class="absolute -left-4 z-10 bg-white border-2 border-black p-1 shadow hover:bg-opacity-100 transition md:block hidden"
        aria-label="Scroll left"
        :style="{ top: arrowTop, transform: 'translateY(-50%)' }"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <button
        v-if="showRightArrow"
        @click="scrollRight"
        class="absolute -right-4 z-10 bg-white border-2 border-black p-1 shadow hover:bg-opacity-100 transition md:block hidden"
        aria-label="Scroll right"
        :style="{ top: arrowTop, transform: 'translateY(-50%)' }"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <div ref="productWrapper" class="sky__product-wrapper">
        <div
          v-for="(product) in [...showcase.hits]"
          :key="product.sku"
          class="sky__product-card"
        >
          <product-card
            :product="product"
            class="mb-5"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductCard from '../general/ProductCard'
import throttle from 'lodash.throttle'

export default {
  components: {
    ProductCard
  },
  props: {
    right: {
      required: false,
      default: false,
      type: Boolean
    },
    showcase: {
      required: false,
      default: null,
      type: Object
    }
  },
  data () {
    return {
      showLeftArrow: false,
      showRightArrow: false,
      arrowTop: '50%',
      throttledUpdateArrows: null,
      throttledHandleResize: null
    }
  },
  mounted () {
    // Throttle scroll and resize handlers for better performance
    this.throttledUpdateArrows = throttle(this.updateArrows, 100, { leading: true, trailing: true })
    this.throttledHandleResize = throttle(this.handleResize, 200)

    this.updateArrows()
    this.updateArrowPosition()
    this.$refs.productWrapper.addEventListener('scroll', this.throttledUpdateArrows)
    window.addEventListener('resize', this.throttledHandleResize)
  },
  beforeDestroy () {
    // Cancel throttled functions and remove listeners
    if (this.throttledUpdateArrows) {
      this.throttledUpdateArrows.cancel()
      this.$refs.productWrapper?.removeEventListener('scroll', this.throttledUpdateArrows)
    }
    if (this.throttledHandleResize) {
      this.throttledHandleResize.cancel()
      window.removeEventListener('resize', this.throttledHandleResize)
    }
  },
  methods: {
    updateArrows() {
      const el = this.$refs.productWrapper
      if (!el) return
      this.showLeftArrow = el.scrollLeft > 5
      this.showRightArrow = el.scrollLeft + el.clientWidth < el.scrollWidth - 5
    },
    scrollLeft() {
      const el = this.$refs.productWrapper
      if (!el) return
      el.scrollTo({ left: 0, behavior: 'smooth' })
    },
    scrollRight() {
      const el = this.$refs.productWrapper
      if (!el) return
      const card = el.querySelector('.sky__product-card')
      if (!card) return
      const scrollAmount = card.offsetWidth * 2 + parseFloat(getComputedStyle(el).gap || 0) * 2
      el.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    },
    updateArrowPosition() {
      this.$nextTick(() => {
        const wrapper = this.$refs.productWrapper
        if (!wrapper) return
        const firstCard = wrapper.querySelector('.sky__product-card')
        if (!firstCard) return
        // Try to find the image inside product-card
        const img = firstCard.querySelector('img')
        if (img) {
          const cardRect = firstCard.getBoundingClientRect()
          const imgRect = img.getBoundingClientRect()
          // Calculate top relative to .big-container
          const containerRect = wrapper.parentElement.getBoundingClientRect()
          this.arrowTop = (imgRect.top + imgRect.height / 2 - containerRect.top) + 'px'
        } else {
          // fallback to center
          this.arrowTop = '50%'
        }
      })
    },
    handleResize() {
      this.updateArrows()
      this.updateArrowPosition()
    }
  }
}
</script>

<style lang="scss" scoped>
.sky__product-wrapper {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 1.7rem; /* gap-5 */
  scroll-behavior: smooth;
  /* Hide scrollbar for Webkit browsers */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for Firefox */
  scrollbar-width: none;
  /* Hide scrollbar for IE/Edge */
  -ms-overflow-style: none;
}
.sky__product-card {
  flex: 0 0 calc((100% - 4 * 1.7rem) / 5);
  min-width: 175px;
  max-width: 100%;
}
</style>
